import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { supabase } from '../../supabaseClient';
import { Modal, ModalFooter } from '../ui/modal';
import { FormGroup, FormLabel, FormInput, FormTextarea, FormSelect, FormCheckbox } from '../ui/form';

const EditProjectGroupModal = ({ show, handleClose: onClose, item, onItemUpdated }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_favorite: false,
    parent_project_group_id: null,
    is_archived: false
  });
  const [parentGroups, setParentGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingParents, setLoadingParents] = useState(false);

  useEffect(() => {
    if (show && item) {
      setFormData({
        name: item.name || '',
        description: item.description || '',
        is_favorite: item.is_favorite || false,
        parent_project_group_id: item.parent_project_group_id || null,
        is_archived: item.is_archived || false
      });
      fetchParentGroups(item.id);
    }
  }, [show, item]);

  const fetchParentGroups = async (currentId) => {
    if (!currentId) return;
    setLoadingParents(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('project_groups')
        .select('id, name')
        .neq('id', currentId)
        .order('name');
      if (error) throw error;
      setParentGroups(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingParents(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setFormData({
      name: '',
      description: '',
      is_favorite: false,
      parent_project_group_id: null,
      is_archived: false
    });
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('project_groups')
        .update(formData)
        .eq('id', item.id)
        .select(`
          *,
          projects (
            id,
            name,
            description
          )
        `);
      if (error) throw error;
      onItemUpdated(data[0]);
      handleClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={show} onClose={handleClose} title="Edit Project Group">
      <form onSubmit={handleSubmit}>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <FormGroup>
            <FormLabel htmlFor="name">Name *</FormLabel>
            <FormInput
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
            />
          </FormGroup>

          <FormGroup>
            <FormLabel htmlFor="description">Description</FormLabel>
            <FormTextarea
              id="description"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
          </FormGroup>

          <FormGroup>
            <FormLabel htmlFor="parentGroup">Parent Project Group</FormLabel>
            <FormSelect
              id="parentGroup"
              value={formData.parent_project_group_id || ''}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({
                  ...formData,
                  parent_project_group_id: value === '' ? null : value
                });
              }}
              disabled={loadingParents}
            >
              <option value="">{loadingParents ? 'Loading...' : 'None'}</option>
              {parentGroups.map(pg => (
                <option 
                  key={pg.id} 
                  value={pg.id}
                  disabled={pg.id === item.id}
                >
                  {pg.name}
                </option>
              ))}
            </FormSelect>
          </FormGroup>

          <FormGroup>
            <FormCheckbox
              id="favorite"
              label="Favorite"
              checked={formData.is_favorite}
              onChange={(e) => setFormData({...formData, is_favorite: e.target.checked})}
            />
          </FormGroup>

          <FormGroup>
            <FormCheckbox
              id="archived"
              label="Archived"
              checked={formData.is_archived}
              onChange={(e) => setFormData({...formData, is_archived: e.target.checked})}
            />
          </FormGroup>

          <FormGroup>
            <FormCheckbox
              id="archived"
              label="Archived"
              checked={formData.is_archived}
              onChange={(e) => setFormData({...formData, is_archived: e.target.checked})}
            />
          </FormGroup>

          {item && (
            <div className="text-sm text-gray-500 dark:text-gray-400 italic mt-4">
              <div>Created {moment(item.created_at).fromNow()}</div>
              {item.updated_at && (
                <div>Last modified {moment(item.updated_at).fromNow()}</div>
              )}
            </div>
          )}
        </div>
        
        <ModalFooter>
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default EditProjectGroupModal;